/**
 * Deposit.jsx — receive USDC.
 *
 * Receiving carries no attack surface: a compromised Telegram account can
 * see a deposit address it could already find on-chain. So unlike withdrawal,
 * this screen is fully exposed.
 *
 * The wrong-network warning sits ABOVE the QR and cannot be scrolled past.
 * Sending USDC from Ethereum or Arbitrum to a Polygon address is the most
 * common way people lose money on a deposit screen, and it is unrecoverable.
 */

import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import * as api from "../lib/api";
import * as tg from "../lib/telegram";
import { C, money, Label, Button, Empty } from "../theme";

export default function Deposit() {
  const [data, setData] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [d, w] = await Promise.all([
          api.depositAddress(),
          api.balance().catch(() => null),
        ]);
        setData(d);
        if (w) setWallet(w);
      } catch (e) { setErr(e.message); }
    };
    load();
    return api.poll(load, 15000);
  }, []);

  if (err) return <Empty><span style={{ color: C.red }}>{err}</span></Empty>;
  if (!data) return <Empty>Loading…</Empty>;

  const addr = data.address || data.deposit_address || data.eoa_address || "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(addr);
      setCopied(true); tg.haptic.ok();
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Telegram's webview clipboard is inconsistent across platforms —
      // the Share button below is the reliable fallback.
      setErr("Copy blocked. Use Share instead.");
    }
  };

  return (
    <div style={{ padding: 14 }}>

      <div style={{ border: `1px solid ${C.red}73`,
                    background: "rgba(196,97,74,0.06)",
                    padding: "11px 12px", marginBottom: 14 }}>
        <p style={{ fontSize: 11.5, color: C.red, margin: "0 0 4px",
                    letterSpacing: "0.06em" }}>
          POLYGON NETWORK ONLY
        </p>
        <p style={{ fontSize: 10.5, color: C.dim, margin: 0, lineHeight: 1.65 }}>
          USDC on Polygon. Sending from another chain, or sending a different
          token, means the funds are lost and cannot be recovered.
        </p>
      </div>

      {addr && (
        <div style={{ background: "#fff", padding: 14, display: "flex",
                      justifyContent: "center", marginBottom: 12 }}>
          <QRCodeSVG value={addr} size={150} level="M" />
        </div>
      )}

      <Label>YOUR DEPOSIT ADDRESS</Label>
      <div style={{ border: `0.5px solid ${C.borderHi}`, background: C.bg1,
                    padding: "11px 12px", marginBottom: 8 }}>
        {/* Full address, wrapped rather than truncated. This is the one place
            all 42 characters must be visible — someone verifying against
            their sending wallet needs to compare the whole string. */}
        <p style={{ fontSize: 11.5, color: C.ink, margin: 0,
                    wordBreak: "break-all", lineHeight: 1.6 }}>
          {addr || "—"}
        </p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr",
                    gap: 7, marginBottom: 16 }}>
        <Button onClick={copy}>{copied ? "Copied" : "Copy"}</Button>
        <Button variant="ghost"
                onClick={() => tg.openLink(
                  `https://t.me/share/url?url=${encodeURIComponent(addr)}`)}>
          Share
        </Button>
      </div>

      <Label>BALANCE</Label>
      <div style={{ border: `0.5px solid ${C.border}`, background: C.bg1,
                    marginBottom: 16 }}>
        {/* Three states, not one. Users with running bots wonder where their
            money went; showing the split answers it before they ask. */}
        <Line label="Available" value={money(wallet?.balance ?? wallet?.usdc ?? 0, false)} />
        <Line label="Pending" value={money(wallet?.pending ?? 0, false)}
              color={Number(wallet?.pending || 0) > 0 ? C.amber : C.ink} />
        <Line last label="In open positions"
              value={money(wallet?.in_positions ?? wallet?.locked ?? 0, false)} />
      </div>

      <div style={{ border: `0.5px solid ${C.border}`,
                    background: "rgba(245,166,35,0.04)", padding: "11px 12px" }}>
        <p style={{ fontSize: 10.5, color: C.dim, margin: 0, lineHeight: 1.7 }}>
          Network: Polygon · Token: USDC · Minimum: 1 USDC.
          Withdrawals are made at polyedge.online.
        </p>
      </div>
    </div>
  );
}

const Line = ({ label, value, color, last }) => (
  <div style={{ display: "flex", justifyContent: "space-between",
                padding: "10px 12px",
                borderBottom: last ? "none" : `0.5px solid rgba(245,166,35,0.12)` }}>
    <span style={{ fontSize: 12, color: C.dim }}>{label}</span>
    <span style={{ fontSize: 12, color: color || C.ink }}>{value}</span>
  </div>
);
